import React, { useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, Copy, MessageCircle, Loader2, Link2, Check } from "lucide-react";
import { toast } from "sonner";

export default function Invite() {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");

  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/invite", { phone }); setResult(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const copy = (text, which) => {
    navigator.clipboard.writeText(text);
    setCopied(which); toast.success("Copied to clipboard");
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader eyebrow="Onboarding" title="WhatsApp Invite Generator"
        description="Generate a pre-formatted WhatsApp invitation to welcome new brothers aboard." />

      <Card className="gold-ring glass p-6">
        <Label>Recipient Phone (optional, with country code)</Label>
        <div className="mt-1.5 flex gap-2">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+234 801 234 5678" data-testid="invite-phone-input" />
          <Button onClick={generate} disabled={busy} className="bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="generate-invite-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Leave phone empty to create a shareable link you can send to anyone.</p>

        {result && (
          <div className="mt-6 space-y-4" data-testid="invite-result">
            <div>
              <Label className="text-xs text-muted-foreground">Invitation Message</Label>
              <Textarea readOnly value={result.message} className="mt-1 min-h-[120px] font-mono-data text-xs" />
              <Button variant="outline" size="sm" className="mt-2 gold-ring" onClick={() => copy(result.message, "msg")} data-testid="copy-message-button">
                {copied === "msg" ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />} Copy Message
              </Button>
            </div>

            <div className="rounded-lg border border-[hsl(var(--water))]/30 bg-[hsl(var(--water))]/5 p-3">
              <Label className="flex items-center gap-1 text-xs text-[hsl(var(--water))]"><Link2 className="h-3.5 w-3.5" /> Onboarding Link</Label>
              <p className="mt-1 break-all font-mono-data text-xs">{result.invite_url}</p>
              <Button variant="ghost" size="sm" className="mt-1" onClick={() => copy(result.invite_url, "link")} data-testid="copy-link-button">
                {copied === "link" ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />} Copy Link
              </Button>
            </div>

            <a href={result.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-link-copy-button">
              <Button className="w-full bg-[#25D366] font-700 text-white hover:bg-[#1da851]">
                <MessageCircle className="mr-2 h-4 w-4" /> Open in WhatsApp
              </Button>
            </a>
          </div>
        )}
      </Card>
    </div>
  );
}
